/**
 * Admin feature layout — the ONE place the fail-closed AdminGuard is applied.
 * All 10 admin pages render inside this layout's <Outlet>, so the gate is
 * evaluated exactly once per admin-route subtree, not re-implemented per page
 * (rebuild-lessons.md #2: the per-page admin check is what let grade.html fail
 * OPEN).
 *
 * The layout renders only AFTER the guard confirms { admin: true }. A non-admin
 * or any error never sees the sub-nav or any <Outlet> content.
 *
 * Routing shape (mirrors andavo-dev's nested layout-route pattern, e.g.
 * AdminNavLayout / SettingsLayout): this is the element of the parent `admin`
 * route; the 10 pages are its child routes. The sub-nav is admin-feature-owned
 * (the shell's top-level nav only knows "Admin" exists — see ExpertsNavLayout).
 *
 * SECONDARY-NAV SHAPE: the admin sub-nav is a HORIZONTAL pill-tab bar (the same
 * pattern Expense uses — ExpenseLayout), NOT a fixed side rail. (CRM has no
 * in-module nav anymore — its subpages live on the shell rail as of 2026-07.)
 * Rationale: the shell already owns the ONE mobile hamburger/drawer (primary
 * nav); a second admin-only drawer would need its own trigger in the shared
 * TopBar, which the shell deliberately doesn't expose. A pill bar needs no new
 * chrome and is the established secondary-nav idiom. This replaces the old
 * side-by-side `w-52 shrink-0` aside, which had no breakpoint and squeezed every
 * page on mobile.
 *
 * TWO ROWS, NOT THIRTEEN TABS (2026-08): the bar was one row of 13 sibling pills
 * that overflowed even a desktop viewport and buried User Management — the page
 * admins actually come here for — in the middle of ops tooling. It is now
 * Access / System / Tools (admin-nav.ts ADMIN_GROUPS) with the active group's
 * pages on a second row. Every page kept its path, so existing links still work.
 */
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { cn } from '@andavo/web-ui';
import { AdminGuard } from './AdminGuard';
import { ADMIN_GROUPS, groupForPath } from '../admin-nav';

/**
 * GROUP tab — a segmented control, not a loose text link.
 *
 * The two rows were visually the same control at two sizes, so the hierarchy read
 * as "six similar links" rather than "three sections, and the pages inside the one
 * you're in". The active group is now a raised chip on a recessed track, which is
 * the conventional way to say "this is a mode you are in" and needs no colour
 * knowledge to parse.
 */
function groupTab(isActive: boolean) {
  return cn(
    'whitespace-nowrap rounded-md px-3.5 py-1.5 font-inter font-medium text-sm transition-all',
    // A focus ring matters more here than usual: these are the top-level admin
    // sections, and the row is horizontally scrollable on a phone.
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-action',
    isActive
      ? 'bg-surface-foreground text-content-primary shadow-elevation-100'
      : 'text-content-secondary hover:text-content-primary',
  );
}

/**
 * PAGE tab within the active group. Deliberately a different SHAPE (pill) from the
 * group control (chip) so the two rows can't be mistaken for peers, and at text-sm
 * rather than the old text-xs, which was small enough to read as disabled.
 */
function pageTab(isActive: boolean) {
  return cn(
    'whitespace-nowrap rounded-full px-3 py-1 font-inter text-sm transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-action',
    isActive
      ? 'bg-surface-action-subtle font-medium text-content-action'
      : 'text-content-secondary hover:bg-surface-background hover:text-content-primary',
  );
}

export function AdminLayout() {
  const { pathname } = useLocation();
  const activeGroup = groupForPath(pathname);

  return (
    <AdminGuard>
      {/* data-testid="admin-root" — the authenticated admin chrome root the
          experts-web-e2e suite keys on (selectors.ts TESTID.adminRoot). It is
          INSIDE AdminGuard, so it only ever exists in the DOM once the server-side
          gate has returned { admin: true } — a non-admin (fail-closed Denied path)
          never renders this node, which is exactly the regression guarantee the
          admin.spec asserts. */}
      <div
        className="flex h-full min-h-0 w-full flex-col"
        data-testid="admin-root"
      >
        {/* Secondary nav: horizontal scrollable pill bar. `no-scrollbar` (the
            vendored utility) hides the scroll chrome; the row scrolls touch-only
            on phones where 13 pills overflow 390px. Mirrors the Tabs primitive's
            track styling (rounded-full surface-foreground) so it reads as the
            same control family as CRM/Expense without pulling in the Radix Tabs
            state machine (these are real <NavLink>s — active state is route-driven,
            which is exactly what NavLink gives us for free). */}
        <nav
          aria-label="Admin sections"
          className="shrink-0 border-border-subtle border-b bg-surface-foreground"
        >
          {/* Row 1 — the three groups. Active state is derived from the pathname
              (groupForPath) rather than NavLink's own matching, because a group
              tab points at its FIRST page: /admin/users would otherwise be the
              only path that lights up the Access tab. */}
          <div className="no-scrollbar overflow-x-auto">
            <div className="w-max px-3 pt-3 pb-1">
              <div className="inline-flex items-center gap-1 rounded-lg bg-surface-background p-1">
                {ADMIN_GROUPS.map((group) => {
                  const isActive = activeGroup?.key === group.key;
                  return (
                    // A plain Link, NOT NavLink: the group tab points at its FIRST
                    // page, so NavLink's own matching marks it active only on that
                    // one path — and it OVERRIDES any aria-current passed in, so a
                    // NavLink here would silently drop the group's real state.
                    // Active is resolved from the pathname (groupForPath) for both
                    // the styling and the announcement.
                    <Link
                      aria-current={isActive ? 'page' : undefined}
                      className={groupTab(isActive)}
                      data-testid={`admin-group-${group.key}`}
                      key={group.key}
                      to={group.path}
                    >
                      {group.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
          {/* Row 2 — the pages inside the active group. A single-page group needs
              no second row (nothing to choose between). */}
          {activeGroup && activeGroup.items.length > 1 ? (
            <div className="no-scrollbar overflow-x-auto">
              <div className="flex w-max items-center gap-0.5 px-3 pt-1 pb-2.5">
                {activeGroup.items.map((item) => (
                  <NavLink
                    className={({ isActive }) => pageTab(isActive)}
                    end={item.end}
                    key={item.path}
                    to={item.path}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ) : (
            <div className="pb-2.5" />
          )}
        </nav>
        {/* min-h-0 lets this flex child own the scroll; safe-area-aware bottom
            padding clears the iOS home indicator (viewport-fit=cover). The shell's
            <main> already pads sm:p-8, so here we keep a tighter p-4 sm:p-6 and
            only add the inset on top of the base. */}
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-6 sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]">
          <Outlet />
        </div>
      </div>
    </AdminGuard>
  );
}
